import { useState } from 'react';
import { Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCompletedClients, useCompletedEngagements } from '@/api/hooks';
import { DataGrid, Column } from '@/components/DataGrid';
import { fmtDateTime } from '@/lib/format';
import { encodeEngId } from '@/lib/engId';

type Row = Record<string, unknown>;

const clientCols: Column<Row>[] = [
  { key: 'RecordType', header: 'Request Type' },
  { key: 'ClientID', header: 'Client ID' },
  { key: 'ClientName', header: 'Client Name' },
  { key: 'CreatedDate', header: 'Submitted', render: (r) => fmtDateTime(r.CreatedDate) },
  { key: 'MaconomyStatus', header: 'Maconomy' },
  { key: 'ConstantStatus', header: 'Constant' },
  { key: 'ClientManagerStatus', header: 'Client Manager' },
  { key: 'ReturnManagerStatus', header: 'Return Manager' },
  { key: 'CompletionDate', header: 'Completed', render: (r) => fmtDateTime(r.CompletionDate) },
  { key: 'CreatedBy', header: 'Requested By' },
  { key: 'CompletedBy', header: 'Completed By' },
];
const engCols: Column<Row>[] = [
  { key: 'RecordType', header: 'Request Type' },
  { key: 'ClientID', header: 'Client ID' },
  { key: 'ClientName', header: 'Client Name' },
  { key: 'CreatedDate', header: 'Submitted', render: (r) => fmtDateTime(r.CreatedDate) },
  { key: 'MaconomyStatus', header: 'Maconomy' },
  { key: 'CCHEngagementStatus', header: 'CCH Eng' },
  { key: 'EngagementLetterStatus', header: 'EL' },
  { key: 'CompletionDate', header: 'Completed', render: (r) => fmtDateTime(r.CompletionDate) },
  { key: 'CompletedBy', header: 'Completed By' },
];

export function Completed() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const clients = useCompletedClients(q);
  const engagements = useCompletedEngagements(q);

  // Row-level edit, like the Power Apps Completed Requests screen: navigates to
  // the same client / engagement edit screens.
  const viewEdit = (to: string) => (
    <button title="View / Edit" className="text-primary hover:opacity-70" onClick={() => navigate(to)}>
      <Eye className="h-4 w-4" />
    </button>
  );

  return (
    // Two grids share the screen height and each scrolls on its own (no page scrollbar
    // on a normal screen; the page only scrolls when the window is very short).
    <div className="flex h-full flex-col gap-6 p-6">
      <div className="flex flex-shrink-0 items-end gap-2">
        <div className="flex-1 max-w-md">
          <label className="label">Search (name / ID / requested / completed by)</label>
          <input className="input" value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setQ(search)} />
        </div>
        <button className="btn-secondary" onClick={() => setQ(search)}>Search</button>
      </div>

      <section className="flex min-h-[240px] flex-1 flex-col">
        <h2 className="mb-2 flex-shrink-0 text-base font-semibold text-foreground">Completed Client Requests</h2>
        <DataGrid columns={clientCols} rows={clients.data ?? []} loading={clients.isFetching}
          heightClass="min-h-0 flex-1"
          rowAction={(r) => viewEdit(`/clients/${r.ClientID}/${r.ClientSubID ?? '0'}`)} />
      </section>

      <section className="flex min-h-[240px] flex-1 flex-col">
        <h2 className="mb-2 flex-shrink-0 text-base font-semibold text-foreground">Completed Engagement Requests</h2>
        <DataGrid columns={engCols} rows={engagements.data ?? []} loading={engagements.isFetching}
          heightClass="min-h-0 flex-1"
          rowAction={(r) => viewEdit(`/engagements/${encodeEngId(String(r.EngagementID))}`)} />
      </section>
    </div>
  );
}
